import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/staff/Button";
import CheckBox from "../../../components/staff/CheckBox";
import Close from "../../../components/Atomos/Icons/Close";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import Log from "../../../components/Atomos/Icons/Log";
import ShapeEdit from "../../../components/Atomos/Icons/ShapeEdit";
import { TrashCan } from "../../../components/Atomos/Icons/TrashCan";
import Modal from "../../../components/Atomos/Modals/Modal";
import { useDynamicPageMeta } from "../../../hooks/useDynamicPageMeta";
import { getFirmById } from "../../../Services/Firm";
import { firmShortLabel } from "../../../utils/firmDisplayName";
import {
  deleteSuggestedProfessionals,
  getFirmProfessionalsReview,
  MAX_DELETE_BATCH,
  type FirmProfessionalRow,
  type FirmProfessionalsReview,
} from "../../../Services/IntegratorFirm";
import type { IntegratorRunLogAssetLink } from "../../../Services/IntegratorRunLog";
import { toastError, toastSuccess } from "../../../utils/toast";
import ProfessionalUserLogs from "../../ManageFirm/Professionals/ProfessionalUserLogs";
import AssetCard, { AssetPreviewModal, type AssetPreviewTarget } from "../runLogs/AssetCard";
import { PANEL, SECTION_TITLE, TABLE_CELL, TABLE_HEAD, TABLE_ROW } from "../runLogs/staffSurface";
import {
  DEFAULT_PROFESSIONALS_SORT,
  nextSort,
  SORT_LABEL,
  sortProfessionals,
  type ProfessionalsSort,
  type ProfessionalsSortKey,
} from "./professionalsReviewSort";

const LIST_ROUTE = "/integrator-engine/firms";
const EMPTY = "—";
const MODAL_BUTTON_CLASS = "w-full justify-center sm:w-[12.5rem]";

/** The Manage Firm professional form; it returns here with `navigate(-1)`. */
function createProfessionalPath(firmId: string): string {
  return `/ManageFirm/Professionals/${firmId}/Create`;
}

function editProfessionalPath(firmId: string, personId: string): string {
  return `/ManageFirm/Professionals/${firmId}/Edit/${personId}`;
}

interface SectionDef {
  key: keyof Pick<
    FirmProfessionalsReview,
    "dataAdmins" | "newlyCreated" | "existing" | "suggestedDeletions"
  >;
  title: string;
  hint: string;
  empty: string;
}

const SECTIONS: SectionDef[] = [
  {
    key: "dataAdmins",
    title: "Data Admins",
    hint: "Professionals who manage the firm's data.",
    empty: "The firm has no data admins.",
  },
  {
    key: "newlyCreated",
    title: "Newly created profiles",
    hint: "Created by the sync and without a login yet.",
    empty: "No profiles are waiting for a login.",
  },
  {
    key: "existing",
    title: "Existing professionals",
    hint: "Active professionals already established in the firm.",
    empty: "The firm has no other professionals.",
  },
  {
    key: "suggestedDeletions",
    title: "Suggested deletions",
    hint: "No longer in the firm's feed. Nothing is deleted until you select professionals and choose Delete.",
    empty: "No professionals are suggested for deletion.",
  },
];

const SORT_KEYS: ProfessionalsSortKey[] = ["lastName", "office", "role"];

export default function ManageIntegratorProfessionals(): ReactElement {
  const { firmId = "" } = useParams();
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  const [firmName, setFirmName] = useState("");
  const [review, setReview] = useState<FirmProfessionalsReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState<ProfessionalsSort>(DEFAULT_PROFESSIONALS_SORT);
  const [logTarget, setLogTarget] = useState<FirmProfessionalRow | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: firmName ? `${firmName} — Professionals` : "Professionals",
      breadcrumbLabels: firmId
        ? {
            [`/integrator-engine/professionals/${firmId}`.toLowerCase()]:
              firmName || "Professionals",
          }
        : {},
    });
    return () => clearPageMeta();
  }, [firmId, firmName, setPageMeta, clearPageMeta]);

  const load = useCallback(async () => {
    if (!firmId) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [firm, next] = await Promise.all([
        getFirmById(firmId).catch(() => undefined),
        getFirmProfessionalsReview(firmId),
      ]);
      if (firm) {
        setFirmName(firmShortLabel(firm));
      }
      setReview(next);
      const suggestedIds = new Set(next.suggestedDeletions.map((row) => row.id));
      setSelected((current) => new Set(Array.from(current).filter((id) => suggestedIds.has(id))));
    } catch (loadError: any) {
      setError(loadError?.message ?? "Could not load the firm's professionals.");
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => {
    if (!review) {
      return null;
    }
    return Object.fromEntries(
      SECTIONS.map((section) => [section.key, sortProfessionals(review[section.key], sort)])
    ) as Record<SectionDef["key"], FirmProfessionalRow[]>;
  }, [review, sort]);

  const suggestedIds = useMemo(
    () => (review ? review.suggestedDeletions.map((row) => row.id) : []),
    [review]
  );

  const toggleOne = (personId: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(personId);
      }
      else {
        next.delete(personId);
      }
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(suggestedIds) : new Set());
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setPendingDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    const personIds = pendingDelete;
    if (!personIds || personIds.length === 0 || isDeleting) {
      return;
    }
    setIsDeleting(true);
    try {
      const results = await deleteSuggestedProfessionals({ firmId, personIds });
      toastSuccess(
        `${results.length} ${results.length === 1 ? "professional" : "professionals"} deleted.`
      );
      setSelected((current) => {
        const next = new Set(current);
        personIds.forEach((id) => next.delete(id));
        return next;
      });
      setPendingDelete(null);
      await load();
    } catch (deleteError: any) {
      toastError(deleteError?.message ?? "No professionals were deleted.", {
        autoClose: 10000,
      });
      setPendingDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteCount = pendingDelete?.length ?? 0;

  return (
    <>
      <Modal
        isModalOpen={pendingDelete != null}
        toggleModal={closeDeleteModal}
        title={deleteCount === 1 ? "Delete professional" : "Delete professionals"}
        titleColor="text-secondary font-semibold text-lg"
        compact
        outerPaddingOnly
        compactBodyScrolls={false}
        className="w-full p-10 !max-w-md !overflow-visible border border-gray-200 shadow-lg sm:!max-w-lg"
        bodyClassName="!pt-0"
      >
        <div className="flex flex-col gap-4">
          <p className="text-left text-sm leading-relaxed text-[#474F56] sm:text-base">
            {deleteCount === 1
              ? "Are you sure you want to delete this person?"
              : `Are you sure you want to delete these ${deleteCount} people?`}
          </p>
          <p className="text-left text-xs text-gray-500">
            They leave the firm's active roster and can no longer sign in. Their data is kept so
            they can be restored. If any of them cannot be deleted, none are.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              className={MODAL_BUTTON_CLASS}
              text="No"
              onClick={closeDeleteModal}
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              disabled={isDeleting}
            />
            <Button
              className={MODAL_BUTTON_CLASS}
              text={isDeleting ? "Deleting…" : "Yes"}
              onClick={() => void handleConfirmDelete()}
              color="danger"
              variant="filled"
              size="fixed"
              disabled={isDeleting}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isModalOpen={logTarget != null}
        toggleModal={() => setLogTarget(null)}
        title={logTarget ? `Change log — ${logTarget.name}` : "Change log"}
        className="max-w-[1200px]"
        bodyClassName="min-h-0 !pt-0 !px-0"
      >
        {logTarget ? (
          <ProfessionalUserLogs
            key={logTarget.id}
            entityId={logTarget.id}
            embeddedInScrollContainer
          />
        ) : null}
      </Modal>

      <div className="flex min-w-0 flex-col">
        <div className="flex w-full items-center justify-between rounded-t-lg bg-accent-Default-dark px-5 py-2.5 text-white">
          <p className="font-decimal-medium text-base">{firmName || firmId}</p>
        </div>

        <div className="flex flex-col gap-5 bg-white p-5">
          {loading && !review ? (
            <p className="text-sm text-gray-600">Loading…</p>
          ) : error ? (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          ) : (
            review &&
            sorted && (
              <section className={PANEL}>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className={`${SECTION_TITLE} !mb-0`}>Manage professionals</h2>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <SortControl sort={sort} onChange={setSort} />
                    <Button
                      className="w-full justify-center sm:w-auto"
                      text="Add Professional"
                      color="secondary"
                      variant="filled"
                      size="fixed"
                      icon={<Plus />}
                      onClick={() => navigate(createProfessionalPath(firmId))}
                    />
                  </div>
                </div>
                <DeletionSourceNote review={review} />
                <div className="flex flex-col gap-3">
                  {SECTIONS.map((section) => {
                    const isDeletions = section.key === "suggestedDeletions";
                    return (
                      <ProfessionalsSection
                        key={section.key}
                        title={section.title}
                        hint={section.hint}
                        count={sorted[section.key].length}
                      >
                        {isDeletions && sorted[section.key].length > 0 && (
                          <DeletionToolbar
                            selectedCount={selected.size}
                            disabled={isDeleting}
                            onDelete={() => setPendingDelete(Array.from(selected))}
                          />
                        )}
                        <ProfessionalsTable
                          firmId={firmId}
                          rows={sorted[section.key]}
                          empty={section.empty}
                          sort={sort}
                          onSort={(key) => setSort((current) => nextSort(current, key))}
                          onOpenLog={setLogTarget}
                          selection={
                            isDeletions
                              ? {
                                  selected,
                                  disabled: isDeleting,
                                  onToggle: toggleOne,
                                  onToggleAll: toggleAll,
                                  onDelete: (row) => setPendingDelete([row.id]),
                                }
                              : undefined
                          }
                        />
                      </ProfessionalsSection>
                    );
                  })}
                </div>
              </section>
            )
          )}

          {review && <DeletionsStoredFile link={review.deletionsFile ?? null} />}

          <div className="flex justify-end border-t border-gray-100 pt-4">
            <Button
              className="w-full justify-center sm:w-[12.5rem]"
              text="Back"
              color="neutral"
              variant="outlined"
              size="fixed"
              icon={<Close />}
              onClick={() => navigate(LIST_ROUTE)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function SortControl({
  sort,
  onChange,
}: {
  sort: ProfessionalsSort;
  onChange: (sort: ProfessionalsSort) => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-font">
      <label htmlFor="professionals-sort">Sort by</label>
      <select
        id="professionals-sort"
        className="rounded border border-gray-300 px-2 py-1"
        value={sort.key}
        onChange={(event) =>
          onChange({ key: event.target.value as ProfessionalsSortKey, direction: "asc" })
        }
      >
        {SORT_KEYS.map((key) => (
          <option key={key} value={key}>
            {SORT_LABEL[key]}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="rounded border border-gray-300 px-2 py-1"
        aria-label={sort.direction === "asc" ? "Ascending" : "Descending"}
        onClick={() => onChange(nextSort(sort, sort.key))}
      >
        {sort.direction === "asc" ? "↑" : "↓"}
      </button>
    </div>
  );
}

function DeletionsStoredFile({
  link,
}: {
  link: IntegratorRunLogAssetLink | null;
}): ReactElement {
  const [previewTarget, setPreviewTarget] = useState<AssetPreviewTarget | null>(null);
  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Stored files</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <AssetCard
          title="Deleted professionals"
          description="Full profile of every professional deleted from this screen."
          kind="json"
          link={link}
          onPreview={() => {
            if (!link) {
              return;
            }
            setPreviewTarget({ title: "Deleted professionals", kind: "json", link });
          }}
        />
      </div>
      <AssetPreviewModal target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </section>
  );
}

function DeletionSourceNote({ review }: { review: FirmProfessionalsReview }): ReactElement {
  if (review.deletionSource) {
    const finished = review.deletionSource.finishedDate
      ? new Date(review.deletionSource.finishedDate).toLocaleString("en-US")
      : null;
    return (
      <p className="mb-3 text-xs text-gray-500">
        Deletion suggestions come from the last successful run
        {finished ? ` (${finished})` : ""}.{" "}
        <Link
          to={`/integrator-engine/run-logs/${review.deletionSource.runLogId}`}
          className="text-secondary"
        >
          Open that run
        </Link>
      </p>
    );
  }
  return (
    <p className="mb-3 text-xs text-gray-500">
      {review.deletionSourceIssue
        ? `Deletion suggestions could not be read: ${review.deletionSourceIssue}`
        : "The firm has no successful run yet, so there are no deletion suggestions."}
    </p>
  );
}

function ProfessionalsSection({
  title,
  hint,
  count,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  children: ReactNode;
}): ReactElement {
  return (
    <details className="rounded border border-gray-200 bg-white" open={count > 0}>
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-font font-decimal-medium">
        {title}
        <span className="ml-2 font-normal text-gray-500">({count})</span>
      </summary>
      <div className="border-t border-gray-100 px-4 py-3">
        <p className="mb-3 text-xs text-gray-500">{hint}</p>
        {children}
      </div>
    </details>
  );
}

function DeletionToolbar({
  selectedCount,
  disabled,
  onDelete,
}: {
  selectedCount: number;
  disabled: boolean;
  onDelete: () => void;
}): ReactElement {
  const overCap = selectedCount > MAX_DELETE_BATCH;
  return (
    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-gray-500">
        {selectedCount} selected
        {overCap && (
          <span className="ml-2 text-red-700">
            Select at most {MAX_DELETE_BATCH} at a time.
          </span>
        )}
      </p>
      <Button
        className="w-full justify-center sm:w-auto"
        text={disabled ? "Deleting…" : "Delete"}
        color="danger"
        variant="filled"
        size="fixed"
        icon={<TrashCan />}
        disabled={selectedCount === 0 || overCap || disabled}
        tooltip="Delete the selected professionals"
        onClick={onDelete}
      />
    </div>
  );
}

interface DeletionSelection {
  selected: Set<string>;
  disabled: boolean;
  onToggle: (personId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onDelete: (row: FirmProfessionalRow) => void;
}

function ProfessionalsTable({
  firmId,
  rows,
  empty,
  sort,
  onSort,
  onOpenLog,
  selection,
}: {
  firmId: string;
  rows: FirmProfessionalRow[];
  empty: string;
  sort: ProfessionalsSort;
  onSort: (key: ProfessionalsSortKey) => void;
  onOpenLog: (row: FirmProfessionalRow) => void;
  /** Only Suggested deletions: checkboxes and a Delete action per row. */
  selection?: DeletionSelection;
}): ReactElement {
  const allSelected =
    selection != null && rows.length > 0 && rows.every((row) => selection.selected.has(row.id));
  return (
    <div className="max-h-96 overflow-auto">
      <table className="w-full border-collapse text-sm text-gray-font">
        <thead>
          <tr>
            {selection && (
              <th className={`${TABLE_HEAD} w-10`}>
                <CheckBox
                  checked={allSelected}
                  disabled={rows.length === 0 || selection.disabled}
                  tooltip="Select all"
                  onCheckedChange={selection.onToggleAll}
                />
              </th>
            )}
            <th className={TABLE_HEAD}>Rewrite ID</th>
            <th className={TABLE_HEAD}>Legacy ID</th>
            <SortableHead label="Name" sortKey="lastName" sort={sort} onSort={onSort} />
            <SortableHead label="Office" sortKey="office" sort={sort} onSort={onSort} />
            <SortableHead label="Role" sortKey="role" sort={sort} onSort={onSort} />
            <th className={TABLE_HEAD}>Display</th>
            <th className={`${TABLE_HEAD} ${selection ? "w-24" : "w-20"}`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={selection ? 8 : 7} className={`${TABLE_CELL} italic text-gray-500`}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className={TABLE_ROW}>
                {selection && (
                  <td className={TABLE_CELL}>
                    <CheckBox
                      checked={selection.selected.has(row.id)}
                      disabled={selection.disabled}
                      onCheckedChange={(checked) => selection.onToggle(row.id, checked)}
                    />
                  </td>
                )}
                <td className={TABLE_CELL}>{row.rewriteId ?? EMPTY}</td>
                <td className={TABLE_CELL}>{row.legacyId || EMPTY}</td>
                <td className={TABLE_CELL}>
                  <Link to={`/staff/profile/attorney/${row.id}`} className="text-secondary">
                    {row.name || EMPTY}
                  </Link>
                </td>
                <td className={TABLE_CELL}>{row.office || EMPTY}</td>
                <td className={TABLE_CELL}>{row.role}</td>
                <td className={TABLE_CELL}>{row.display ? "Yes" : "No"}</td>
                <td className={TABLE_CELL}>
                  <div className="flex items-center gap-3 text-gray-font">
                    <Link
                      to={editProfessionalPath(firmId, row.id)}
                      className="flex items-center"
                      aria-label={`Modify ${row.name}`}
                      title="Modify"
                    >
                      <div className="h-4 w-4">
                        <ShapeEdit />
                      </div>
                    </Link>
                    <button
                      type="button"
                      className="flex items-center"
                      aria-label={`View change log for ${row.name}`}
                      title="Change log"
                      onClick={() => onOpenLog(row)}
                    >
                      <div className="h-4 w-4">
                        <Log />
                      </div>
                    </button>
                    {selection && (
                      <button
                        type="button"
                        className="flex items-center text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Delete ${row.name}`}
                        title="Delete"
                        disabled={selection.disabled}
                        onClick={() => selection.onDelete(row)}
                      >
                        <div className="h-4 w-4">
                          <TrashCan />
                        </div>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: ProfessionalsSortKey;
  sort: ProfessionalsSort;
  onSort: (key: ProfessionalsSortKey) => void;
}): ReactElement {
  const active = sort.key === sortKey;
  return (
    <th
      className={TABLE_HEAD}
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className="flex items-center gap-1 font-semibold"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className={active ? "" : "opacity-40"}>
          {active && sort.direction === "desc" ? "↓" : "↑"}
        </span>
      </button>
    </th>
  );
}
